import { Trans, useLingui } from '@lingui/react/macro';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import UrlInput from 'ud-ui/components/url-input/url-input';
import { mapEventTypeToMatchEventType } from 'modules/games/domain/data-mappers/game-event-type.data-mapper';
import { GameDetails, GameEvent } from 'modules/games/domain/interfaces/game-details';
import { setMomentUrl, setTimestampUrl } from 'modules/games/store/game-details';
import generateMomentUrl from 'modules/games/utils/generate-moment-url';
import { buildMomentUrlsFromMarkers, MomentEvent } from 'modules/video-markers/domain/utils';
import { useMatchVideoMarkers } from 'modules/video-markers/ui/hooks';
import { TransformUrl } from 'shared/utils';
import { theme } from 'styles/theme/theme';

interface IProps {
  isLoading: boolean;
  game: GameDetails | null;
}

/** Откуда берутся таймкоды: из видеометок трансляции или от начала таймов, введённого руками. */
type MomentSource = 'markers' | 'periods';

const keyFirstPeriod = '1';
const keySecondPeriod = '2';

export default function TimestampUrls(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLingui();

  const { isLoading, game } = props;
  const timestampUrls = game?.timestamps ?? {};
  const firstPeriodRef = React.createRef<HTMLInputElement>();
  const secondPeriodRef = React.createRef<HTMLInputElement>();

  const [isOpen, setIsOpen] = useState(false);
  const [chosenSource, setChosenSource] = useState<MomentSource | null>(null);

  const { markers, isLoading: isMarkersLoading } = useMatchVideoMarkers(game?.id);
  const streamUrl = game?.videoUrls[0] ?? '';
  // Метки одной записи без эфира (`streaming_ms` пуст) ссылку на трансляцию не дают.
  const hasMarkers = useMemo(() => markers.some((row) => row.streamingMs !== null), [markers]);
  const isStreamUrlSupported = TransformUrl.getDomain(streamUrl.trim()) !== null;
  const canUseMarkers = hasMarkers && isStreamUrlSupported;
  // Пока пользователь не выбрал сам, видеометки — там, где они есть: начала таймов вводить не нужно.
  const source: MomentSource = chosenSource ?? (canUseMarkers ? 'markers' : 'periods');

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    close();
    dispatch(setTimestampUrl({ key: keyFirstPeriod, url: firstPeriodRef.current?.value ?? '' }));
    dispatch(setTimestampUrl({ key: keySecondPeriod, url: secondPeriodRef.current?.value ?? '' }));
  }, [close, dispatch, firstPeriodRef, secondPeriodRef]);

  const replaceUrlsByPeriods = useCallback(
    (events: GameEvent[]) => {
      for (const event of events) {
        switch (event.period) {
          case 1:
            const eventUrl = generateMomentUrl({ event, periodStartUrl: firstPeriodRef.current?.value ?? '' });
            if (!eventUrl) return;
            dispatch(setMomentUrl({ eventId: event.id, url: eventUrl }));
            break;
          case 2:
            const eventUrl2 = generateMomentUrl({ event, periodStartUrl: secondPeriodRef.current?.value ?? '' });
            if (!eventUrl2) return;
            dispatch(setMomentUrl({ eventId: event.id, url: eventUrl2 }));
            break;
        }
      }
    },
    [dispatch, firstPeriodRef, secondPeriodRef],
  );

  const replaceUrlsByMarkers = useCallback(
    (events: GameEvent[]) => {
      const momentEvents = events.flatMap((event): MomentEvent[] => {
        const type = mapEventTypeToMatchEventType(event.type);
        return type ? [{ id: event.id, period: event.period, seconds: event.seconds, type }] : [];
      });
      const urls = buildMomentUrlsFromMarkers({ events: momentEvents, markers, streamUrl });

      urls.forEach((url, eventId) => dispatch(setMomentUrl({ eventId, url })));
      if (urls.size === 0) {
        toast.warning(t`Ни одно событие не нашлось в видеометках трансляции`);
      }
    },
    [dispatch, markers, streamUrl, t],
  );

  const replaceEventUrls = source === 'markers' ? replaceUrlsByMarkers : replaceUrlsByPeriods;

  const handleReplaceAllEventUrl = useCallback(() => {
    replaceEventUrls(game?.events ?? []);
    close();
  }, [close, game?.events, replaceEventUrls]);

  const handleReplaceEmptyEventUrl = useCallback(() => {
    replaceEventUrls(game?.events.filter((event) => !event.videoUrl) ?? []);
    close();
  }, [close, game?.events, replaceEventUrls]);

  const markersHint = useMemo(() => {
    if (isMarkersLoading) return null;
    if (!hasMarkers) return t`Для этого матча нет видеометок`;
    if (!isStreamUrlSupported) return t`Вставьте ссылку на трансляцию из youtube или vk-video`;
    return t`Таймкоды — по видеометкам трансляции, начала таймов не нужны`;
  }, [hasMarkers, isMarkersLoading, isStreamUrlSupported, t]);

  return (
    <>
      <div className={'flex flex-col'}>
        <div className={'mb-4 flex w-full justify-end'}>
          <UDButton variant={'secondary'} onClick={open}>
            <Trans>Сгенерировать ссылки</Trans>
          </UDButton>
        </div>
      </div>

      <Modal show={isOpen} onClose={onClose} size="md">
        <Modal.Body>
          <div className={'flex flex-col gap-2'}>
            <div className={'flex flex-row gap-2'}>
              <UDButton
                variant={source === 'markers' ? 'primary' : 'secondary'}
                aria-pressed={source === 'markers'}
                disabled={!canUseMarkers}
                loading={isMarkersLoading}
                onClick={() => setChosenSource('markers')}
              >
                <Trans>Из видеометок</Trans>
              </UDButton>
              <UDButton
                variant={source === 'periods' ? 'primary' : 'secondary'}
                aria-pressed={source === 'periods'}
                onClick={() => setChosenSource('periods')}
              >
                <Trans>По началу таймов</Trans>
              </UDButton>
            </div>
            {markersHint && (
              <UDText type={'caption-1'} className={'text-inkSecondary'}>
                {markersHint}
              </UDText>
            )}

            {/* Поля не размонтируются: при закрытии окна их значения сохраняются в `timestamps`. */}
            <div className={source === 'periods' ? 'mt-2 flex flex-col gap-2' : 'hidden'}>
              <div className={'flex flex-row gap-2'}>
                <UDText type={'subhead'}>
                  <Trans>
                    Ссылки <strong>с тайм кодом</strong> на начало матча
                  </Trans>
                </UDText>
              </div>
              <UDText color={'#828282'} type={'caption-1'}>
                <Trans>Ссылки принимаются из youtube и vk-video</Trans>
              </UDText>
              <UrlInput
                ref={firstPeriodRef}
                isLoading={isLoading}
                name="first-time-url"
                placeholder={t`Ссылка на начало 1-ого тайма`}
                inputProps={{
                  style: { backgroundColor: theme.colors.GrayF2 },
                  defaultValue: timestampUrls[keyFirstPeriod]?.url ?? '',
                }}
              />
              <UrlInput
                ref={secondPeriodRef}
                isLoading={isLoading}
                name="second-time-url"
                placeholder={t`Ссылка на начало 2-ого тайма`}
                inputProps={{
                  style: { backgroundColor: theme.colors.GrayF2 },
                  defaultValue: timestampUrls[keySecondPeriod]?.url ?? '',
                }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant={'primary'} onClick={handleReplaceAllEventUrl}>
            <Trans>Заменить существующие</Trans>
          </UDButton>
          <UDButton variant={'secondary'} onClick={handleReplaceEmptyEventUrl}>
            <Trans>Вставить в пустые</Trans>
          </UDButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}
